import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// The film (media/bridge-film): one picture, two narrations. "Watch" is the
// current voice. "Executive" is the same picture with the plain narration
// (media/bridge-film/narration/executive.json). Opened from the hero; the
// same files play on /watch. Dormant until REACT_APP_FILM_VERSION is set —
// bump it when either cut's files change (/media/** caches for a week,
// file names never change, the query string busts the cache). Chapter
// starts mirror media/bridge-film/shots.json `chapters`.
const BASE = (process.env.REACT_APP_FILM_VIDEO_BASE || '/media/bridge-film').replace(/\/$/, '');
export const FILM_VERSION = process.env.REACT_APP_FILM_VERSION || '';
export const FILM_LENGTH = '3:07';
export const FILM_CUTS = {
  watch: {
    label: 'Watch',
    webm: 'bridge-1080.webm',
    mp4: 'bridge-1080.mp4',
    mobile: 'bridge-720-mobile.mp4',
    captions: 'bridge-captions.vtt',
    bar: 'The bridge to the systems you use and the apps you build. You draw the work: branches, loops, a call. The gate holds, NoVA is on the screen with you, the brain shows how it knew. Then the numbers, measured on our own fleet.',
  },
  executive: {
    label: 'Executive',
    webm: 'bridge-exec-1080.webm',
    mp4: 'bridge-exec-1080.mp4',
    mobile: 'bridge-exec-720-mobile.mp4',
    captions: 'bridge-exec-captions.vtt',
    bar: 'The same film, in plainer words. The systems you use, the apps you build, the approvals, NoVA on the screen, and the numbers measured on our own fleet.',
  },
};
export const filmCut = (id) => FILM_CUTS[id] || FILM_CUTS.watch;
export const CHAPTERS = [
  { at: 0, title: 'The bridge', length: '0:12' },
  { at: 11.83, title: 'The platform', length: '0:35' },
  { at: 46.92, title: 'Govern', length: '0:20' },
  { at: 66.58, title: 'NoVA', length: '0:12' },
  { at: 79, title: 'App Kit', length: '0:23' },
  { at: 101.58, title: 'One brain', length: '0:16' },
  { at: 117.83, title: 'The numbers', length: '1:09' },
];
export const numbersChapter = () => CHAPTERS.find((c) => c.title === 'The numbers') || CHAPTERS[CHAPTERS.length - 1];
export const filmEnabled = () => Boolean(FILM_VERSION);
const file = (name) => `${BASE}/${name}?v=${FILM_VERSION}`;

// The native player: controls, captions on by default, narrow viewports get
// the 720p file; a narration switch and a chapter row sit under it. Shared
// by the lightbox and /watch. `start` opens on a chapter (seconds). `cut`
// selects the narration; switching keeps the playhead.
export function FilmPlayer({ autoPlay = false, start = 0, cut = 'watch', onCut, className = '' }) {
  const mobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches;
  const ref = useRef(null);
  const hold = useRef(0);
  const [t, setT] = useState(start);
  const sources = filmCut(cut);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const to = hold.current || start || 0;
    const resume = hold.current > 0;
    const seek = () => {
      if (to) v.currentTime = to;
      if (autoPlay || resume) v.play().catch(() => {});
    };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    return () => v.removeEventListener('loadedmetadata', seek);
  }, [cut, start, autoPlay]);
  const go = (at) => {
    const v = ref.current;
    if (!v) return;
    v.currentTime = at;
    setT(at);
    v.play().catch(() => {});
  };
  const pick = (next) => {
    if (next === cut) return;
    hold.current = ref.current ? ref.current.currentTime : t;
    if (onCut) onCut(next);
  };
  const current = CHAPTERS.reduce((c, ch) => (t >= ch.at - 0.5 ? ch : c), CHAPTERS[0]);
  return (
    <div className={`cs-film ${className}`}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the captions track is below */}
      <video
        key={cut}
        ref={ref}
        className="cs-results-film"
        controls
        autoPlay={autoPlay}
        playsInline
        preload="metadata"
        poster={file('bridge-poster.jpg')}
        crossOrigin="anonymous"
        onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
      >
        {!mobile && <source src={file(sources.webm)} type="video/webm" />}
        {!mobile && <source src={file(sources.mp4)} type="video/mp4" />}
        {mobile && <source src={file(sources.mobile)} type="video/mp4" />}
        <track kind="captions" src={file(sources.captions)} srcLang="en" label="English" default />
      </video>
      <div className="cs-film-cuts" role="tablist" aria-label="Narration">
        {Object.entries(FILM_CUTS).map(([id, c]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={id === cut}
            aria-label={id === 'executive' ? 'Executive overview' : undefined}
            className={id === cut ? 'is-current' : ''}
            onClick={() => pick(id)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="cs-film-chapters" role="tablist" aria-label="Chapters">
        {CHAPTERS.map((ch, i) => (
          <button key={ch.title} type="button" role="tab" aria-selected={ch === current} className={ch === current ? 'is-current' : ''} onClick={() => go(ch.at)}>
            <span>{i + 1}</span>{ch.title}<small>{ch.length}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilmLightbox({ open, start = 0, cut = 'watch', onCut, onClose }) {
  const closeRef = useRef(null);
  const voice = filmCut(cut);

  // Esc closes; the page behind does not scroll while the film is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="cs-film-lightbox" role="dialog" aria-modal="true" aria-label="The film" onClick={onClose}>
      <div className="cs-film-lightbox-stage" onClick={(e) => e.stopPropagation()}>
        <FilmPlayer autoPlay start={start} cut={cut} onCut={onCut} />
        <div className="cs-film-lightbox-bar">
          <span>{voice.bar}</span>
          <a href={cut === 'executive' ? '/watch?cut=executive' : '/watch'}>Open on its own page</a>
        </div>
      </div>
      <button ref={closeRef} type="button" className="cs-film-lightbox-close" onClick={onClose} aria-label="Close">
        &times;
      </button>
    </div>,
    document.body
  );
}
